// eslint-disable-next-line import/no-cycle
import {
  ActionTypeEnum, FacebookMessengerButton, FieldConfig, ItemsVerticalAlignValueEnum, ListSelectionTypeEnum, ResponsiveDisplayStackEnum,
} from '@/types';
// eslint-disable-next-line import/no-cycle
import { getSectionIdsFromIframe, TemplateEditorState } from '@/composables/template-editor/TemplateEditor';
// eslint-disable-next-line import/no-cycle
import createTimedLRUCache from '@/helpers/createTimedLRUCache';
// eslint-disable-next-line import/no-cycle
import { LINE_HEIGHT_POSSIBLE_VALUES, SELECTION_TYPE_FIELD_ID, TRANSLATION_MARKUP } from '@/components/template-builder/utils/constants';
// eslint-disable-next-line import/no-cycle
import {
  getParentContentWidth, getSectionParent, getTemplateIframeDocument, isEmailTemplate,
} from '../utils/helpers';
// eslint-disable-next-line import/no-cycle
import { getDynamicStylesForActiveSection, getDynamicStylesForSelector } from '../utils/parser';
// eslint-disable-next-line import/no-cycle
import { sanitizeTranslationMarkup } from '../utils/translate';
import { DATA_VAR_SRC } from '../utils/variable-image';

// eslint-disable-next-line import/no-cycle
export * from './social-networks';
// eslint-disable-next-line import/no-cycle
export * from './link';
// eslint-disable-next-line import/no-cycle
export * from './smart-product-list';
// eslint-disable-next-line import/no-cycle
export * from './form';
// eslint-disable-next-line import/no-cycle
export * from './menu';

/**
 * Hybrid getter for game widget visual properties.
 * Reads from CSS custom property (supports mobile/desktop) with fallback to data-* attribute.
 */
export const getGameCssProperty = (selector: string, name: string, onlyValue = true, mobile = false): string | undefined => {
  const cssVarName = `--spm-game-${name.replace(/^data-/, '')}`;
  const dynamicStyles = getDynamicStylesForSelector(selector, mobile);
  if (dynamicStyles[cssVarName]) {
    return dynamicStyles[cssVarName];
  }
  // Fallback: read data-* attribute from DOM (backward compat for existing templates)
  const template = getTemplateIframeDocument();
  const element: HTMLElement | null = template?.querySelector(selector);
  return element?.getAttribute(name) ?? undefined;
};

export const getAttributeByName = (selector: string, attribute: string): string|undefined => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement|null = template?.querySelector(selector);
  if (element) {
    // For 'src' attribute, prefer data-var-src if it exists (contains raw variable expression)
    if (attribute === 'src') {
      const varSrc = element.getAttribute(DATA_VAR_SRC);
      if (varSrc) {
        return varSrc;
      }
    }
    return element.getAttribute(attribute) ?? undefined;
  }
  return undefined;
};

export const getAlwaysTrue = (): string => 'true';

/**
 * Getters for scratch card cover image — maps background-* properties to data-cover-image-* attributes.
 * Used with BackgroundImage component.
 */
export const getGameCoverImageProp = (selector: string, property: string): string | undefined => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement | null = template?.querySelector(selector);
  if (!element) return undefined;
  if (property === 'background-image') {
    const url = element.getAttribute('data-cover-image');
    return url ? `url("${url}")` : 'none';
  }
  if (property === 'background-size') {
    return element.getAttribute('data-cover-image-size') || 'cover';
  }
  if (property === 'background-position-x' || property === 'background-position-y') {
    return element.getAttribute('data-cover-image-position') || 'center';
  }
  if (property === 'background-repeat') {
    return 'no-repeat';
  }
  return undefined;
};

/**
 * Getter for scratch card cover icon — maps background-image to img src.
 * Used with BackgroundImage component (NO_OPTION type).
 */
export const getGameCoverIconProp = (selector: string, property: string): string | undefined => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement | null = template?.querySelector(selector);
  if (!element) return undefined;
  if (property === 'background-image') {
    const iconAttr = element.getAttribute('data-cover-icon');
    if (iconAttr) {
      return `url("${iconAttr}")`;
    }
    const iconEl = element.querySelector('img.spm_game_scratch_card_cover_icon') as HTMLElement | null;
    if (iconEl) {
      const src = iconEl.getAttribute('src');
      return src ? `url("${src}")` : 'none';
    }
    return 'none';
  }
  return undefined;
};

/**
 * Getter for push notification data-redirect attribute.
 * Handles both URL/variable values (stored as-is) and template redirect IDs (base64, reconstructed with #redirect_ prefix).
 */
export const getPushLinkValue = (selector: string, attribute: string): string | undefined => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement | null = template?.querySelector(selector);
  if (element) {
    const value = element.getAttribute(attribute);
    if (!value) return undefined;
    // Check if value is a base64-encoded redirect ID (decodes to a numeric template ID)
    try {
      const decoded = atob(value);
      if (/^\d+$/.test(decoded)) {
        return `#redirect_${value}`;
      }
    } catch (e) {
      // Not valid base64 → not a redirect ID
    }
    // Everything else (URL, variable, etc.) → return as-is
    return value;
  }
  return undefined;
};

export const getDecodedDataRedirect = (selector: string, attribute: string): string => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement|null = template?.querySelector(selector);
  if (element) {
    const value = element.getAttribute(attribute) ?? '';
    let returnValue;
    try {
      returnValue = atob(value.replace('base64:', '').replace('#redirect_', '') ?? '');
    } catch (err) {
      returnValue = value;
    }
    return returnValue;
  }
  return '';
};

const cssPropertyCache = createTimedLRUCache<string, any>({
  timeout: 1500,
  maxSize: 500,
});

export const clearCssPropertyCache = () => {
  cssPropertyCache.clear();
};

export const invalidateCssPropertyCacheForSelector = (selector: string, propertyName: string, mobile = false) => {
  // Invalidate both variants (onlyValue true and false)
  const cacheKey1 = `${selector}|${propertyName}|1|${mobile ? 'mobile' : 'desktop'}`;
  const cacheKey0 = `${selector}|${propertyName}|0|${mobile ? 'mobile' : 'desktop'}`;
  cssPropertyCache.delete(cacheKey1);
  cssPropertyCache.delete(cacheKey0);
};

export const getCssPropertyByName = (selector: string, name: string, onlyValue = true, mobile = false): { type: string; value: any } | string | undefined => {
  const cacheKey = `${selector}|${name}|${onlyValue ? '1' : '0'}|${mobile === true ? 'mobile' : 'desktop'}`;
  const cached = cssPropertyCache.get(cacheKey);
  if (cached !== undefined) {
    return cached;
  }
  const dynamicStyles = getDynamicStylesForSelector(selector, mobile);
  const element = getTemplateIframeDocument().querySelectorAll(selector)[0];

  let returnValue;
  let value;
  if (Object.prototype.hasOwnProperty.call(dynamicStyles, name) && dynamicStyles[name]) {
    value = dynamicStyles[name];
  } else if (element) {
    if (name === 'background' && Object.prototype.hasOwnProperty.call(dynamicStyles, 'background-color')
      && dynamicStyles['background-color'] && Object.prototype.hasOwnProperty.call(dynamicStyles, 'background-image') && !dynamicStyles['background-image'].match('gradient')) {
      // eslint-disable-next-line no-param-reassign
      name = 'background-color';
      value = dynamicStyles['background-color'];
    } else {
      const css = window.getComputedStyle(element);
      value = css.getPropertyValue(name);
      if (mobile === true) {
        value = dynamicStyles[name] || value;
      }
    }
  }

  if (element && name === 'max-width' && element.tagName === 'IMG') {
    const currentWidth = (isEmailTemplate(TemplateEditorState.template?.type) ? dynamicStyles.width : dynamicStyles['max-width']);
    if ((new RegExp('%$')).test(currentWidth)) {
      value = currentWidth;
    } else if (element.tagName === 'IMG' && value && value.match('px$')) {
      const currentImgWidthAttr = (isEmailTemplate(TemplateEditorState.template?.type) ? element.getAttribute('width') : currentWidth);
      if (currentImgWidthAttr && parseInt(currentImgWidthAttr, 10) < parseInt(value, 10)) {
        value = `${currentImgWidthAttr}px`;
      }
    }
  } else if (element && value && name === 'line-height') {
    // Try to get the font-size from dynamic styles first
    let fontSizeValue = dynamicStyles['font-size'];
    const allowedValues = LINE_HEIGHT_POSSIBLE_VALUES.map((v) => v.value); // Allowed normalized line-height values

    // Fallback to computed font-size if dynamic one is missing
    if (!fontSizeValue) {
      const css = window.getComputedStyle(element);
      fontSizeValue = css.getPropertyValue('font-size');
    }

    const fontSize = parseFloat(fontSizeValue);

    // Convert percentage line-height to multiplier
    if (value && /%$/.test(value)) {
      const lineHeightPercent = parseFloat(value);
      value = (lineHeightPercent / 100).toFixed(2);
      // eslint-disable-next-line brace-style
    } else if (value && /px$/.test(value)) {
      const lineHeightPx = parseFloat(value);
      if (fontSize > 0) {
        value = (lineHeightPx / fontSize).toFixed(2);
      } else {
        value = '1'; // fallback if font-size is invalid
      }
    }

    // Normalize to closest allowed line-height multiplier
    if (name === 'line-height' && value) {
      const numericValue = parseFloat(value);
      value = allowedValues.reduce((prev, curr) => (Math.abs(parseFloat(curr) - numericValue) < Math.abs(parseFloat(prev) - numericValue) ? curr : prev));
    }
  } else if (element && name === 'justify-content' && value === 'normal') {
    value = ItemsVerticalAlignValueEnum.START;
  }

  // Avoid return false value of background color (default is rgba(0, 0, 0, 0) is no background color set)
  if (value && name === 'background-color' && value === 'rgba(0, 0, 0, 0)') {
    value = '';
  }
  if (value) {
    let hasInDynamicStyles = !!dynamicStyles[name];

    // Special handling for border-radius: check individual corner properties
    if (name === 'border-radius' && !hasInDynamicStyles) {
      const borderRadiusCorners = [
        'border-top-left-radius',
        'border-top-right-radius',
        'border-bottom-left-radius',
        'border-bottom-right-radius',
      ];
      hasInDynamicStyles = borderRadiusCorners.some((corner) => !!dynamicStyles[corner]);
    }

    returnValue = onlyValue ? value : { type: hasInDynamicStyles ? 'element' : 'parent', value };
  }

  // Define the CSS properties that need special handling for comma separation
  const backgroundProps = ['background-repeat', 'background-position-x', 'background-position-y', 'background-size'];
  const isBackgroundProp = backgroundProps.includes(name);
  const isBackground = name === 'background';

  if (returnValue) {
    // Utility function to remove everything after the first comma
    const replaceComma = (value2: string) => value2.replace(/,.*$/, '');

    // Utility function to extract gradient information
    const handleGradient = (value2: string) => value2.replace(/.*((linear|radial)-gradient.*\))$/, '$1');

    if (typeof returnValue === 'string') {
      if (isBackgroundProp) {
        // Remove everything after the first comma for specific background properties
        returnValue = replaceComma(returnValue);
      } else if (isBackground) {
        if (returnValue.match(/(linear|radial)-gradient/)) {
          // Extract gradient information for background property
          returnValue = handleGradient(returnValue);
        } else {
          // Replace with background-color if no gradient is found
          const returnValuString = getCssPropertyByName(selector, 'background-color', true, mobile);
          if (typeof returnValuString === 'string') {
            returnValue = returnValuString;
          }
        }
      }
    } else if (typeof returnValue === 'object' && typeof returnValue.value === 'string') {
      if (isBackgroundProp) {
        // Remove everything after the first comma for specific background properties
        returnValue.value = replaceComma(returnValue.value);
      } else if (isBackground) {
        if (returnValue.value.match(/(linear|radial)-gradient/)) {
          // Extract gradient information for background property
          returnValue.value = handleGradient(returnValue.value);
        } else {
          // Replace with background-color if no gradient is found
          const returnValuString = getCssPropertyByName(selector, 'background-color', true, mobile);
          if (typeof returnValuString === 'string') {
            returnValue.value = returnValuString;
          }
        }
      }
    }
  }
  cssPropertyCache.set(cacheKey, returnValue);
  return returnValue;
};

/**
 * Width for fields sizing an element relative to its container. A width no stylesheet declares is a
 * layout result: reported in px, it gets written back as a fixed width that pins the element to its
 * desktop size on every client, so it is reported relative to the parent instead.
 */
export const getRelativeWidth = (selector: string, name: string, onlyValue = true, mobile = false): { type: string; value: any } | string | undefined => {
  const width = getCssPropertyByName(selector, name, false, mobile);
  if (!width || typeof width !== 'object') {
    return width;
  }

  let { value } = width;
  const element = getTemplateIframeDocument().querySelector(selector);
  if (width.type === 'parent' && element && /px$/.test(value)) {
    const parentWidth = getParentContentWidth(element as HTMLElement);
    if (parentWidth > 0) {
      value = `${Math.min(100, Math.round((parseFloat(value) / parentWidth) * 100))}%`;
    }
  }

  return onlyValue ? value : { type: width.type, value };
};

export const getBackgroundCssProperty = (selector: string, name: string, onlyValue = true): { type: string; value: any } | string | undefined => {
  let returnValue;
  returnValue = getCssPropertyByName(selector, name, onlyValue);
  if (typeof returnValue === 'string') {
    if (returnValue.match(/(linear|radial)-gradient/)) {
      returnValue = returnValue.replace(/.*((linear|radial)-gradient.*\))$/, '$1');
    } else {
      returnValue = getCssPropertyByName(selector, 'background-color', true);
    }
    if (typeof returnValue === 'string') {
      returnValue = returnValue.replace('!important', '').trim();
    }
  }
  return returnValue;
};

export const getContentWithLineBreaks = (selector: string): string | undefined => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement | null = template?.querySelector(selector);

  if (element) {
    // Récupérer et nettoyer le contenu sans retraiter les balises <br>
    const sanitizedContent = sanitizeTranslationMarkup(element).innerHTML;
    // Remplacer uniquement les <br> par \n une seule fois
    return sanitizedContent.replace(/\n/gi, '').replace(/<br\s*\/?>/gi, '\n');
  }
  return undefined;
};

export const getContent = (selector: string) => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement|null = template?.querySelector(selector);

  if (element) {
    // This getter can be involved by translation
    return sanitizeTranslationMarkup(element).innerHTML;
  }
  return undefined;
};

export const getText = (selector: string) => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement|null = template?.querySelector(selector);

  if (element) {
    // This getter can be involved by translation
    // Returns only text content without HTML tags (e.g., excludes icons)
    return sanitizeTranslationMarkup(element).textContent || undefined;
  }
  return undefined;
};

export const getClassByName = (selector: string, name: string): string|undefined => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement|null = template?.querySelector(selector);
  if (element) {
    const classes = element.getAttribute('class')?.split(' ');
    const currentClass = classes?.filter((className: string) => className.startsWith(name))[0];
    return currentClass?.replace(name, '');
  }
  return undefined;
};

export const hasClass = (selector: string, name: string, options: any = null): boolean => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement|null = template?.querySelector(selector);
  const returnValue = element?.classList.contains(name) ?? false;
  return typeof options === 'object' && options !== null && options.revert === true ? !returnValue : returnValue;
};

export const hasClassElementAndChild = (selector: string, name: string): boolean => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement|null = template?.querySelector(selector);

  const resolvedSelector = selector.includes(',')
    ? selector
      .split(',') // Séparer les sélecteurs s'il y a une virgule
      .map((sel) => `${sel.trim()} .${name}`) // Ajouter `.name` à chaque sélecteur
      .join(',') // Reconstituer le sélecteur
    : `${selector} .${name}`; // Sinon, ajouter directement `.name`
  const childElement: HTMLElement | null = template?.querySelector(resolvedSelector);
  return (element?.classList.contains(name) || childElement !== null) ?? false;
};

export const getTagType = (selector: string): string|null => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement|null = template?.querySelector(selector);
  if (element) {
    return element.tagName.toLowerCase();
  }
  return null;
};

export const getFacebookButtons = (selector: string): Array<object> => {
  const template = getTemplateIframeDocument();
  const buttonsContainer: HTMLElement|null = template?.querySelector(selector);
  const buttonsConfiguration: Array<object> = [];

  if (buttonsContainer) {
    const buttons: Array<HTMLElement> = Array.from(buttonsContainer.querySelectorAll('.button'));

    buttons.forEach((button: HTMLElement) => {
      const dataRedirect = button.getAttribute('data-redirect') ?? '';
      const text = sanitizeTranslationMarkup(button).innerHTML;

      const newButton: FacebookMessengerButton = {
        action: dataRedirect.match(new RegExp('#redirect_')) ? ActionTypeEnum.DISPLAY_TEMPLATE : ActionTypeEnum.REDIRECT,
        dataRedirect: dataRedirect.match(new RegExp('(#redirect_)|(base64:)'))
          ? atob(dataRedirect.replace('base64:', '').replace('#redirect_', ''))
          : dataRedirect,
        text,
        selectedTemplate: null,
      };

      buttonsConfiguration.push(newButton);
    });
  }

  return buttonsConfiguration;
};

export const isVisible = (items: Array<FieldConfig>, targetId: string) => {
  const targetField = items.find((item) => item.fieldId === targetId);
  if (targetField) {
    const option = targetField.properties[0].value !== undefined ? targetField.properties[0].value.toString() : Object.values(targetField.options).pop()?.defaultValue ?? '';
    return option.toString() === targetField.enableValue;
  }
  return false;
};

const DETERMINISTIC_SELECTION_TYPES: string[] = [
  ListSelectionTypeEnum.PRODUCTS_CART,
  ListSelectionTypeEnum.PRODUCTS_ORDER,
  ListSelectionTypeEnum.MANUAL_SELECTION,
];

// Cart/order reminders and manual selections restore chosen content: product exclusions do not apply to them.
export const isRecommendationSelection = (items: Array<FieldConfig>) => {
  const engine = items
    .find((item) => item.fieldId === SELECTION_TYPE_FIELD_ID)
    ?.properties.find((property) => property.name === 'engine')?.value;
  return !DETERMINISTIC_SELECTION_TYPES.includes(engine);
};

export const isSmartListExclusionVisible = (items: Array<FieldConfig>, targetId: string) => isVisible(items, targetId) && isRecommendationSelection(items);

export const getBackgroundImageProperties = (selector: string, name: string): { type: string; value: any } | string | undefined => {
  // We check if selector is dedicated to a widget or not
  const idWidget = selector.match(new RegExp('#spm_widget_([a-zA-Z0-9]+)'));
  const isWidget = Array.isArray(idWidget) && idWidget.length > 0;

  let currentSelector = selector;

  getSectionIdsFromIframe(!isWidget).forEach((sectionId) => {
    // eslint-disable-next-line radix
    if (sectionId) {
      const dynamicStyles = getDynamicStylesForActiveSection(sectionId);
      if (dynamicStyles) {
        const { sheet } = dynamicStyles;
        if (sheet) {
          Object.values(sheet.cssRules).forEach((styleRule: any) => {
            if (
              styleRule.selectorText.match(new RegExp(selector))
              && styleRule.style['backgroundImage' as any]
            ) {
              currentSelector = styleRule.selectorText;
            }
          });
        }
      }
    }
  });

  return currentSelector;
};

export const getBackgroundImageValue = (selector: string, name: string): { type: string; value: any } | string | undefined => {
  // We check if selector is dedicated to a widget or not
  const idWidget = selector.match(new RegExp('#spm_widget_([a-zA-Z0-9]+)'));
  const isWidget = Array.isArray(idWidget) && idWidget.length > 0;

  let value = '';

  // First check CSS dynamic styles
  getSectionIdsFromIframe(!isWidget).forEach((sectionId) => {
    // eslint-disable-next-line radix
    if (sectionId) {
      const dynamicStyles = getDynamicStylesForActiveSection(sectionId);
      if (dynamicStyles) {
        const { sheet } = dynamicStyles;
        if (sheet) {
          Object.values(sheet.cssRules).forEach((styleRule: any) => {
            if (
              typeof styleRule.selectorText === 'string'
              && styleRule.selectorText.match(new RegExp(selector))
              && styleRule.style['backgroundImage' as any]
            ) {
              value = styleRule.style.backgroundImage;
            }
          });
        }
      }
    }
  });

  // For email templates, also check HTML background attribute (URL is stored there, not in CSS)
  if (!value && isEmailTemplate(TemplateEditorState.template?.type)) {
    const template = getTemplateIframeDocument();
    const element = template?.querySelector(selector) as HTMLElement;
    if (element) {
      const bgAttr = element.getAttribute('background');
      if (bgAttr) {
        value = `url("${bgAttr}")`;
      }
    }
  }

  return value;
};

export const isMediaDisplayAlreadyRestricted = (selector: string) => {
  const template = getTemplateIframeDocument();
  let isMediaRestricted = false;
  if (template) {
    const element = template.querySelector(selector);
    if (element) {
      const widgets = Array.from(element.querySelectorAll('[data-widgettype]'));
      widgets.forEach((widget) => {
        if (Array.from(widget.classList).some((cls) => cls.startsWith('show-for-'))) {
          isMediaRestricted = true;
        }
      });
      if (isMediaRestricted) {
        return 'templateBuilder.fields.warningMessages.mobileDisplayAlreadyRestrictedFromWidgets';
      }

      const sectionParent = element.parentElement ? getSectionParent(element.parentElement) : null;
      if (sectionParent && Array.from(sectionParent.classList).some((cls) => cls.startsWith('show-for-'))) {
        return 'templateBuilder.fields.warningMessages.mobileDisplayAlreadyRestrictedFromSections';
      }
    }
  }
  return isMediaRestricted;
};

export const getResponsiveDisplay = (selector: string, name: string): string => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement|null = template?.querySelector(selector);

  const resolvedSelector = selector.includes(',')
    ? selector
      .split(',') // Séparer les sélecteurs s'il y a une virgule
      .map((sel) => `${sel.trim()} .${name}`) // Ajouter `.name` à chaque sélecteur
      .join(',') // Reconstituer le sélecteur
    : `${selector} .${name}`; // Sinon, ajouter directement `.name`
  const childElement: HTMLElement | null = template?.querySelector(resolvedSelector);
  const isResponsive = (element?.classList.contains(name) || childElement !== null) ?? false;
  if (!isResponsive) {
    return ResponsiveDisplayStackEnum.NO_STACK;
  }
  const column = element?.querySelector('.columns');
  if (column && column.getAttribute('dir') === ResponsiveDisplayStackEnum.RTL) {
    return ResponsiveDisplayStackEnum.RTL;
  }
  return ResponsiveDisplayStackEnum.LTR;
};

export const hasAttribute = (selector: string, attribute: string): boolean => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement|null = template?.querySelector(selector);
  if (element) {
    return element.hasAttribute(attribute);
  }
  return false;
};

export const getRichTextingCta = (selector: string, name: string) => {
  const template = getTemplateIframeDocument();
  const ctaListContainer = template.querySelector(selector);

  if (!ctaListContainer) return [];

  const ctaElements = ctaListContainer.querySelectorAll('.rcs-message__cta__item');

  return Array.from(ctaElements).map((element) => {
    const spmTranslationId = element.querySelector(TRANSLATION_MARKUP)?.getAttribute('id');
    return {
      translationId: spmTranslationId?.replace('LANG_', '').split('.')[0],
      value: sanitizeTranslationMarkup(element as HTMLElement).innerHTML,
      action: {
        type: element.getAttribute('data-rcs-action-type'),
        value: element.getAttribute('href'),
      },
      description: element.getAttribute('data-rcs-url-description') || '',
    };
  });
};

export const getWhatsappCta = (selector: string, name: string) => {
  const template = getTemplateIframeDocument();
  const ctaListContainer = template.querySelector(selector);

  if (!ctaListContainer) return [];

  const ctaElements = ctaListContainer.querySelectorAll('.whatsapp-message__cta__item');

  return Array.from(ctaElements).map((element) => {
    const spmTranslationId = element.querySelector(TRANSLATION_MARKUP)?.getAttribute('id');
    return {
      translationId: spmTranslationId?.replace('LANG_', '').split('.')[0],
      value: sanitizeTranslationMarkup(element as HTMLElement).innerHTML,
      action: {
        type: element.getAttribute('data-whatsapp-action-type'),
        value: element.getAttribute('href'),
      },
    };
  });
};

export const isAttributeActive = (selector: string, attribute: string): boolean => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement|null = template?.querySelector(selector);
  if (element) {
    const elementAttribute = element.getAttribute(attribute);
    return elementAttribute === 'true';
  }
  return false;
};

/**
 * Get height only if explicitly defined in dynamic styles (not computed)
 * Returns empty string if height is not explicitly set (toggle is off)
 */
export const getExplicitHeight = (selector: string, name: string): string => {
  // Handle multiple selectors (comma-separated)
  const selectors = selector.split(',').map((s) => s.trim());

  for (const sel of selectors) {
    const dynamicStyles = getDynamicStylesForSelector(sel, false);
    if (dynamicStyles && dynamicStyles[name] && dynamicStyles[name] !== 'auto') {
      return dynamicStyles[name];
    }
  }

  return '';
};

/**
 * Get background position as grid value (tl, tc, tr, ml, mc, mr, bl, bc, br)
 * Converts CSS background-position to grid position
 */
export const getBackgroundPositionAsGrid = (selector: string): string => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement | null = template?.querySelector(selector);
  if (!element) return 'mc';

  const computedStyle = window.getComputedStyle(element);
  const bgPosition = computedStyle.backgroundPosition || 'center center';

  // Map CSS background-position to grid values
  const cssToGrid: Record<string, string> = {
    'left top': 'tl',
    '0% 0%': 'tl',
    'center top': 'tc',
    '50% 0%': 'tc',
    'right top': 'tr',
    '100% 0%': 'tr',
    'left center': 'ml',
    '0% 50%': 'ml',
    'center center': 'mc',
    '50% 50%': 'mc',
    'right center': 'mr',
    '100% 50%': 'mr',
    'left bottom': 'bl',
    '0% 100%': 'bl',
    'center bottom': 'bc',
    '50% 100%': 'bc',
    'right bottom': 'br',
    '100% 100%': 'br',
  };

  return cssToGrid[bgPosition] || 'mc';
};

export const getMarginAlignment = (selector: string): string => {
  const dynamicStyles = getDynamicStylesForSelector(selector, false);
  const marginLeft = dynamicStyles['margin-left'];
  const marginRight = dynamicStyles['margin-right'];

  if (marginLeft === 'auto' && marginRight === 'auto') {
    return 'center';
  }
  if (marginLeft === 'auto') {
    return 'right';
  }
  return 'left';
};

export interface ValidationRuleData {
  type: string;
  value: string;
  message: string;
  translationKey: string;
}

export const getValidationRules = (selector: string): ValidationRuleData[] => {
  const template = getTemplateIframeDocument();
  const widgetEl = template?.querySelector(selector);
  if (!widgetEl) return [];

  const input = widgetEl.querySelector('.spm_widget_input_text__input');
  const messagesContainer = widgetEl.querySelector('.spm_validation_messages');

  const rules: ValidationRuleData[] = [];
  const ruleAttributes = ['minlength', 'maxlength', 'pattern', 'min', 'max'];

  ruleAttributes.forEach((rule) => {
    const attrValue = input?.getAttribute(`data-spm-val-${rule}`);
    if (attrValue !== null && attrValue !== undefined) {
      const msgEl = messagesContainer?.querySelector(`[data-spm-val-rule="${rule}"]`);
      const translationEl = msgEl?.querySelector(TRANSLATION_MARKUP);
      rules.push({
        type: rule,
        value: attrValue,
        message: translationEl?.textContent || '',
        translationKey: translationEl?.id?.replace('LANG_', '') || '',
      });
    }
  });

  return rules;
};
